import React, { useState, useEffect } from 'react';
import { TextField, Button, Box, Typography, Grid, CircularProgress, Alert } from '@mui/material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const VerificationForm = ({ userId }) => {
  const [aadhar, setAadhar] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [formErrors, setFormErrors] = useState({ aadhar: false });

  useEffect(() => {
    if (userId) {
      axios.get(`${API_URL}/verification/`, {
        params: { user_id: userId }
      })
        .then(response => {
          if (response.data && response.data.aadhar) {
            setVerificationData(response.data);
            setAadhar(response.data.aadhar || '');
            setLoading(false);
          } else {
            setVerificationData(null);
            setLoading(false);
          }
        })
        .catch(err => {
          setLoading(false);
        });
    }
  }, [userId]);

  const handleSubmit = () => {
    setError('');
    setSuccessMessage('');
    setFormErrors({});

    if (!aadhar || aadhar.trim().length !== 12 || isNaN(aadhar)) {
      setFormErrors({
        aadhar: true,
      });
      setError('Please enter a valid 12-digit Aadhar number.');
      return;
    }

    const isNewData = !verificationData;
    const apiUrl = isNewData
      ? `${API_URL}/verification/create/`
      : `${API_URL}/verification/update/`;

    axios({
      method: isNewData ? 'POST' : 'PATCH',
      url: apiUrl,
      data: {
        user_id: userId,
        aadhar: aadhar,
      },
    })
      .then((response) => {
        if (response.data?.message) {
          setSuccessMessage(response.data.message);
        } else {
          setSuccessMessage('Aadhar data saved successfully.');
        }

        setIsEditing(false);
        setFormErrors({ aadhar: false });

        if (!isNewData) {
          setVerificationData({ ...verificationData, aadhar });
        } else {
          setVerificationData(response.data.verification || { aadhar });
        }
      })
      .catch((error) => {
        const serverMessage =
          error.response?.data?.error || error.response?.data?.message;

        setError(serverMessage || 'Failed to save Aadhar verification data.');
        setFormErrors({ aadhar: true });

        console.error('Aadhar verification error:', error);
      });
  };


  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '10vh', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 3, }}>

      {verificationData && !isEditing && (
        <Box sx={{ marginBottom: 3 }}>
          <Typography sx={{ color: '#e0e0e0', fontSize: '1.1rem', marginBottom: 1, letterSpacing: 4 }}>Aadhar: {verificationData.aadhar}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              sx={{
                backgroundColor: '#ff8c00',
                color: '#fff',
                padding: 1.5,
                mt: 3,
                textAlign: 'center',
                borderRadius: '30px',
                letterSpacing: 3,
                '&:hover': { backgroundColor: '#ff6f00' },
              }}
              onClick={() => setIsEditing(true)}
            >
              Edit Information
            </Button>
          </Box>
        </Box>
      )}

      {(isEditing || !verificationData) && (
        <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
          <Grid item xs={12}>
            <TextField
              label="Aadhar Number"
              variant="outlined"
              fullWidth
              value={aadhar}
              onChange={(e) => setAadhar(e.target.value)}
              error={formErrors.aadhar}
              helperText={formErrors.aadhar ? 'Aadhar number is required' : ''}
              sx={{
                letterSpacing: 4,
                '& .MuiInputBase-root': {
                  backgroundColor: '#000000',
                  color: '#e0e0e0',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#777',
                },
                '& .MuiInputLabel-root': {
                  color: 'white',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'white', // Ensure label stays white on focus
                },
                '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ff8c00',
                },
                '& .MuiFormHelperText-root': {
                  color: '#ff8c00',
                },
              }}
            />

          </Grid>

        </Grid>
      )}

      {error && <Alert severity="error">{error}</Alert>}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}

      {(isEditing || !verificationData) && (
        <Button
          sx={{
            backgroundColor: '#ff8c00',
            color: '#fff',
            fontWeight: 'bold',
            padding: 1.5,
            borderRadius: '30px',
            marginTop: 3,
            letterSpacing: 3,
            '&:hover': { backgroundColor: '#ff6f00' },
          }}
          onClick={handleSubmit}
        >
          {verificationData ? 'Save Changes' : 'Submit'}
        </Button>
      )}
    </Box>
  );
};

export default VerificationForm;
